import API from 'utils/api';
import getFormattedValues, { formatReceived, hasFiles } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';

export const baseURI = '/estructura-organizacional/empleados';
export const URI = '/estructura-organizacional/actualizar-estado-empleado';

const getValuesAndConfig = (values) => {
  const files = ['foto', 'firma_electronica'];
  const config = hasFiles(values, files) ? {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  } : {};
  const formattedValues = getFormattedValues(values, {
    files,
    formData: config.headers,
    clean: false,
  });
  return [formattedValues, config];
};

/**
 * GET Empleados
 * @param {object} params
 * @returns {Promise<object[]>}
 */
export const getEmpleados = async (params) => {
  try {
    const response = await API.get(baseURI, { params });
    return { ...response.data, results: formatReceived(response.data.results) };
  } catch (err) {
    onError(err);
  }
  return [];
};

/**
 * GET Empleado
 * @param {number} id
 * @returns {Promise<object|null>}
 */
export const getEmpleado = async (id) => {
  try {
    const response = await API.get(`${baseURI}/${id}`);
    return response.data;
  } catch (err) {
    onError(err);
  }
  return null;
};

/**
 * POST Empleado
 * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const postEmpleado = async (values) => {
  try {
    const response = await API.post(baseURI, ...getValuesAndConfig(values));
    // onSuccess(response, 'Agregado correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * PATCH Empleado
 * @param {number} id
 * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const patchEmpleado = async (id, values) => {
  try {
    const response = await API.patch(`${baseURI}/${id}`, ...getValuesAndConfig(values));
    // onSuccess(response, 'Editado correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * PATCH Empleado
 * @param {number} EmpleadoId
 * @param {number} estadoId
 * @returns {Promise<object|boolean>}
 */
export const patchEstadoEmpleado = async (empleadoId, estadoId) => {
  try {
    const response = await API.patch(`${URI}/${empleadoId}/${estadoId}`);
    onSuccess(response, 'Estatus del Empleado actualizado');
    return true;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * DELETE Empleado
 * @param {number} id
 * @returns {Promise<boolean>}
 */
export const deleteEmpleado = async (id) => {
  try {
    const response = await API.delete(`${baseURI}/${id}`);
    onSuccess(response, 'Eliminado correctamente');
    return true;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * GET Empleados Optimizado {raw query}
 * @param {object} params
 * @returns {Promise<object[]>}
 */
export const getEmpleadosOptimized = async (params) => {
  try {
    const response = await API.get('/estructura-organizacional/empleados-optimized', { params });
    return response.data;
  } catch (err) {
    onError(err);
  }
  return [];
};

/**
 * GET Empleados Optimizado {raw query}
 * @param {object} params
 * @returns {Promise<object[]>}
 */
export const getEmpleadosWithoutFilter = async (params) => {
  try {
    const response = await API.get('/estructura-organizacional/empleados-without-filtered', { params });
    return response.data;
  } catch (err) {
    onError(err);
  }
  return [];
};

/**
 * GET Empleados Optimizado y con filtros {raw query}
 * @param {object} params
 * @returns {Promise<object[]>}
 */
export const getEmpleadosFiltered = async (params) => {
  try {
    const response = await API.get('/estructura-organizacional/empleados-filtered', { params });
    return response.data;
  } catch (err) {
    onError(err);
  }
  return [];
};

export const descargarExcelEmpleados = async () => {
  try {
    const response = await API.get('/estructura-organizacional/descargar-excel-empleados', {
      headers: {
        'Content-Type': 'application/ms-excel',
      },
      responseType: 'arraybuffer',
    });
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * GET Empleados Con Datos Operacionales Filtrados y Paginado
 * @param {object} params
 * @returns {Promise<object[]>}
 */
export const getEmpleadosConDatosOpFilteredPaginated = async (url) => {
  try {
    const response = await API.get(url);
    return response.data;
  } catch (err) {
    onError(err);
  }
  return [];
};

/**
 * GET Empleados Con Datos Operacionales Filtrados y Paginado
 * @param {object} params
 * @returns {Promise<object[]>}
 */
export const getEmpleadosConDatosOpFilteredPaginados = async (params) => {
  try {
    const response = await API.get('catalogos/dato-operacional-del-empleado-paginado/?empleados_filter=', { params });
    return response.data;
  } catch (err) {
    onError(err);
  }
  return [];
};
/**
 * GET Todos los empleados, recorriendo la paginación automáticamente
 * @param {object} params
 * @returns {Promise<object[]>}
 */
export const getTodosLosEmpleados = async (params = {}) => {
  let todos = [];
  let page = 1;
  let hasMore = true;

  try {
    while (hasMore) {
      // eslint-disable-next-line no-await-in-loop
      const data = await getEmpleados({ ...params, page });

      if (!data || !Array.isArray(data.results)) {
        break;
      }

      todos = [...todos, ...data.results];
      hasMore = !!data.next;
      page += 1;
    }
  } catch (err) {
    // getEmpleados ya maneja sus propios errores de red (onError) y nunca
    // debería lanzar; si esto se dispara, es un bug real a investigar.
    // eslint-disable-next-line no-console
    console.error('[getTodosLosEmpleados] error inesperado al paginar empleados', err);
  }

  return { count: todos.length, results: todos };
};

/**
 * GET Empleados Optimizado {raw query}
 * @param {object} params
 * @returns {Promise<object[]>}
 */
/**
 * GET Todos los campos de todas las pestañas de Empleados, una fila por empleado (para el Excel
 * Personalizado). El backend responde compacto ({ llaves, titulos, filas: [[...]] }); aquí se
 * convierte a { filas: [{ llave: valor }], titulos: { llave: título } }.
 * @returns {Promise<{ filas: object[], titulos: object } | null>}
 */
export const getEmpleadosExportarCompleto = async () => {
  try {
    const { data } = await API.get('/estructura-organizacional/empleados-exportar-completo/');
    const llaves = data?.llaves || [];
    const titulos = {};
    llaves.forEach((llave, idx) => { titulos[llave] = data.titulos[idx]; });
    const filas = (data?.filas || []).map((valores) => {
      const fila = {};
      llaves.forEach((llave, idx) => { fila[llave] = valores[idx]; });
      return fila;
    });
    return { filas, titulos };
  } catch (err) {
    onError(err);
  }
  return null;
};

export const getEmpleadosvistaOptimized = async (params) => {
  try {
    const response = await API.get('/estructura-organizacional/empleados-vista-optimized', { params });
    return response.data;
  } catch (err) {
    onError(err);
  }
  return [];
};
